import { makeStyles, shorthands } from "@fluentui/react-components";

export const useStyles = makeStyles({
  card: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    ...shorthands.overflow("hidden"),
  },
  title: {
    marginBottom: "0.25rem",
    fontWeight: 700,
    color: "#201f1e",
  },
  filterRow: {
    display: "flex",
    columnGap: "1rem",
    rowGap: "1rem",
    flexWrap: "wrap",
    alignItems: "flex-end",
    marginTop: "1rem",
    marginBottom: "1.5rem",
  },
  tableWrap: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    // This wrapper (not `.ms-DetailsList`) is the real scroll container for
    // BOTH axes - Fluent's DetailsList root renders data-is-scrollable="false",
    // and its row-virtualization (findScrollableParent in
    // @fluentui/utilities/scroll.js) skips any element carrying that
    // attribute when it looks for what to scroll/measure rows against, so the
    // scroll container has to be an ancestor without that attribute.
    overflowX: "auto",
    overflowY: "auto",
    maxHeight: "30rem",
    display: "block",
    ...shorthands.border("1px", "solid", "#d9d7d5"),
    ...shorthands.borderRadius("8px"),
    // `ConstrainMode.horizontalConstrained` puts its own `overflow-x: auto` on
    // `.ms-DetailsList`. Per the CSS overflow spec, once overflow-x is a
    // non-visible value, the browser silently forces overflow-y away from
    // `visible` too (both must resolve together) - so overriding only
    // overflowY here gets coerced right back to `auto`. Both axes have to be
    // set to `visible` together for this element to truly stop being its own
    // (non-scrolling, and therefore pointless) intermediate overflow context.
    "& .ms-DetailsList": {
      width: "100%",
      overflowX: "visible",
      overflowY: "visible",
      ...shorthands.borderRadius("8px"),
    },
    // Doubled attribute selector beats the global
    // `div[role="grid"] { height:100% !important }` rule (App.css, CF-5808) so
    // the grid sizes to its rows instead of stretching to the wrapper's full
    // scroll height (which left a phantom empty band below the last row).
    "& [role='grid'][role='grid']": {
      width: "100%",
      height: "auto !important",
      maxHeight: "none",
    },
    // Fluent renders the header inside its OWN sibling wrapper
    // (`.ms-DetailsList-headerWrapper`), separate from the row content
    // (`.ms-DetailsList-contentWrapper`) - see DetailsList.base.js. A sticky
    // element's stick range is bounded by its own parent's box; since
    // `headerWrapper` is only ever exactly header-height tall, putting
    // `position: sticky` on `.ms-DetailsHeader` (a child of that wrapper)
    // leaves it no room to stick - the tiny wrapper scrolls past almost
    // immediately and the header goes with it. Making `headerWrapper` ITSELF
    // the sticky element (rather than the header inside it) fixes this -
    // confirmed against a minimal repro of this exact sibling structure.
    "& .ms-DetailsList-headerWrapper": {
      position: "sticky",
      top: 0,
      zIndex: 2,
    },
    "& .ms-DetailsHeader": {
      backgroundColor: "#eef3fb",
      ...shorthands.borderBottom("2px", "solid", "#c7d6ea"),
      height: "52px",
      lineHeight: "52px",
    },
    "& .ms-DetailsHeader-cell": {
      height: "52px",
    },
    "& .ms-DetailsHeader-cellName": {
      fontWeight: 700,
      fontSize: "0.95rem",
      color: "#201f1e",
    },
    "& .ms-DetailsRow": {
      ...shorthands.borderBottom("1px", "solid", "#e1dfdd"),
      minHeight: "56px",
    },
    "& .ms-DetailsRow:hover": {
      backgroundColor: "#f7f9fc",
    },
    '& [data-item-index="-1"]': {
      display: "none",
    },
    "& .ms-DetailsRow-cell": {
      fontSize: "1rem",
      paddingTop: "20px",
      paddingBottom: "20px",
    },
  },
  statusPill: {
    display: "inline-block",
    paddingTop: "0.15rem",
    paddingBottom: "0.15rem",
    paddingLeft: "0.6rem",
    paddingRight: "0.6rem",
    borderTopLeftRadius: "12px",
    borderTopRightRadius: "12px",
    borderBottomLeftRadius: "12px",
    borderBottomRightRadius: "12px",
    fontSize: "0.8rem",
    fontWeight: 600,
  },
  statusActive: {
    backgroundColor: "#dff6dd",
    color: "#107c10",
  },
  statusPending: {
    backgroundColor: "#fff4ce",
    color: "#8a6d00",
  },
  statusAccepted: {
    backgroundColor: "#deecf9",
    color: "#004e8c",
  },
  statusDeactivationRequested: {
    backgroundColor: "#fff4ce",
    color: "#8a6d00",
  },
  statusDeactivated: {
    backgroundColor: "#f3f2f1",
    color: "#605e5c",
  },
  statusRevoked: {
    backgroundColor: "#f3f2f1",
    color: "#605e5c",
  },
  statusDeclined: {
    backgroundColor: "#fde7e9",
    color: "#a80000",
  },
  statusExpired: {
    backgroundColor: "#f3f2f1",
    color: "#605e5c",
  },
  footerRow: {
    marginTop: "0.75rem",
    color: "#605e5c",
    fontSize: "0.85rem",
  },
  mt16: {
    marginTop: "1rem",
  },
  actionCell: {
    display: "flex",
    alignItems: "center",
    columnGap: "0.75rem",
  },
  highlightedRow: {
    backgroundColor: "#fff4ce",
    transitionProperty: "background-color",
    transitionDuration: "3s",
    transitionTimingFunction: "ease-out",
  },
});
